import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Mail,
  Archive,
  ArchiveRestore,
  Trash2,
  Loader2,
  Paperclip,
  ChevronLeft,
  Send,
  Reply,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useDeleteInboxEmail,
  useInboxEmail,
  useReplyInboxEmail,
  useUpdateInboxEmail,
} from '@/hooks/useInbox';
import { useConfirm } from '@/components/ConfirmDialog';
import { ApiError } from '@/lib/api-client';
import { formatDate } from '@/lib/utils';

function extractEmail(raw: string): string {
  const m = raw.match(/<([^>]+)>/);
  return (m ? m[1] : raw).trim();
}

export function PlatformInboxDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const q = useInboxEmail(id);
  const update = useUpdateInboxEmail();
  const del = useDeleteInboxEmail();
  const confirm = useConfirm();

  const email = q.data?.email;
  const bodyError = q.data?.body_fetch_error;
  const busy = update.isPending || del.isPending;

  const onArchiveToggle = async () => {
    if (!email) return;
    await update.mutateAsync({ id: email.id, is_archived: !email.is_archived });
  };

  const onMarkUnread = async () => {
    if (!email) return;
    await update.mutateAsync({ id: email.id, is_read: false });
    navigate({ to: '/platform/inbox' });
  };

  const onDelete = async () => {
    if (!email) return;
    const ok = await confirm({
      title: 'E-postayı sil?',
      description: 'Bu işlem geri alınamaz.',
      confirmText: 'Sil',
      danger: true,
    });
    if (!ok) return;
    await del.mutateAsync(email.id);
    navigate({ to: '/platform/inbox' });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/platform/inbox">
            <ChevronLeft className="w-4 h-4" />
            Gelen Kutusu
          </Link>
        </Button>
      </div>

      {q.isPending ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Yükleniyor…
          </CardContent>
        </Card>
      ) : !email ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            E-posta bulunamadı.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Header */}
          <Card>
            <CardContent className="p-5 space-y-3">
              <h1 className="text-xl font-semibold">{email.subject || '(konu yok)'}</h1>
              <div className="text-sm space-y-0.5">
                <div>
                  <span className="text-muted-foreground">Kimden:</span> {email.from}
                </div>
                <div>
                  <span className="text-muted-foreground">Kime:</span> {email.to.join(', ')}
                </div>
                {email.cc && email.cc.length > 0 && (
                  <div>
                    <span className="text-muted-foreground">Cc:</span> {email.cc.join(', ')}
                  </div>
                )}
                <div className="text-xs text-muted-foreground pt-1">
                  {formatDate(email.received_at)} · ID: {email.id}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <Button size="sm" variant="outline" onClick={onMarkUnread} disabled={busy}>
                  <Mail className="w-3.5 h-3.5" /> Okunmadı olarak işaretle
                </Button>
                <Button size="sm" variant="outline" onClick={onArchiveToggle} disabled={busy}>
                  {email.is_archived ? (
                    <>
                      <ArchiveRestore className="w-3.5 h-3.5" /> Arşivden çıkar
                    </>
                  ) : (
                    <>
                      <Archive className="w-3.5 h-3.5" /> Arşivle
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onDelete}
                  disabled={busy}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 ml-auto"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Sil
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Attachments */}
          {email.attachments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Paperclip className="w-3.5 h-3.5" /> Ekler ({email.attachments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                <ul className="text-sm space-y-1">
                  {email.attachments.map((att, i) => {
                    const a = att as { filename?: string; content_type?: string };
                    return (
                      <li key={i} className="rounded border px-2 py-1 flex items-center gap-2">
                        <Paperclip className="w-3 h-3 text-muted-foreground shrink-0" />
                        <span className="truncate">{a.filename ?? `attachment_${i}`}</span>
                        {a.content_type && (
                          <span className="text-xs text-muted-foreground shrink-0">
                            {a.content_type}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <p className="text-xs text-muted-foreground">
                  Resend ekleri ID olarak gönderir; tam dosyayı indirmek için Resend API gerekir.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Body */}
          <Card>
            <CardContent className="p-0">
              {email.html ? (
                <iframe
                  srcDoc={email.html}
                  sandbox=""
                  className="w-full min-h-[500px] rounded"
                  title="email-body"
                />
              ) : email.text ? (
                <pre className="p-5 whitespace-pre-wrap text-sm font-sans leading-relaxed">
                  {email.text}
                </pre>
              ) : bodyError ? (
                <div className="p-5">
                  <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2">
                    <strong>Gövde çekilemedi.</strong>
                    <div className="mt-1 text-xs font-mono">{bodyError}</div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      Inbound API Key'inin <code className="bg-muted px-1 py-0.5 rounded">inbound:read</code> iznine sahip olduğundan emin ol.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-5 text-sm text-muted-foreground italic">Bu e-postada gövde yok.</div>
              )}
            </CardContent>
          </Card>

          {/* Reply */}
          <ReplyForm
            emailId={email.id}
            defaultTo={extractEmail(email.from)}
            defaultSubject={email.subject ?? ''}
          />
        </>
      )}
    </div>
  );
}

function ReplyForm({
  emailId,
  defaultTo,
  defaultSubject,
}: {
  emailId: string;
  defaultTo: string;
  defaultSubject: string;
}) {
  const reply = useReplyInboxEmail(emailId);
  const [to, setTo] = useState(defaultTo);
  const [subject, setSubject] = useState(
    defaultSubject.toLowerCase().startsWith('re:')
      ? defaultSubject
      : `Re: ${defaultSubject}`,
  );
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setTo(defaultTo);
    setSubject(
      defaultSubject.toLowerCase().startsWith('re:')
        ? defaultSubject
        : `Re: ${defaultSubject}`,
    );
  }, [defaultTo, defaultSubject]);

  const html = useMemo(() => {
    const safe = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return `<div style="font-family:system-ui,Arial,sans-serif;font-size:14px;line-height:1.5;white-space:pre-wrap">${safe}</div>`;
  }, [text]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSent(false);
    try {
      await reply.mutateAsync({
        to,
        subject,
        text,
        html,
      });
      setSent(true);
      setText('');
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gönderilemedi');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Reply className="w-4 h-4" /> Cevapla
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="reply-to">Kime</Label>
            <Input
              id="reply-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reply-subj">Konu</Label>
            <Input
              id="reply-subj"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reply-body">Mesaj</Label>
            <textarea
              id="reply-body"
              rows={8}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed"
              placeholder="Cevabını yaz…"
              required
            />
          </div>
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}
          {sent && (
            <div className="text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 rounded-md px-3 py-2">
              ✓ Cevap gönderildi
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button type="submit" disabled={reply.isPending || !text.trim()}>
              {reply.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Gönder
            </Button>
            <span className="text-xs text-muted-foreground">
              E-posta Ayarları'ndaki gönderim backend'i kullanılır.
            </span>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

